import Player from "@vimeo/player";

var $ = jQuery.noConflict();

$(function () {
  const VIDEO_COOKIE_CONSENT_GROUP = "C0004"; // TARGETING_COOKIES_GROUP
  const COOKIE_GROUPS_UPDATED_EVENT = "boardflow:optanonwrapper";
  const BOARDFLOW_READY_EVENT = "boardflow:ready";
  const BOARDFLOW_READY_TIMEOUT_MS = 2500;
  const LOG_PREFIX = "[VideoWithOverlay]";

  const vimeoPlayers = new Map();
  const vimeoInitializationFailures = new Set();

  // Players are keyed by their video element rather than by its id: blocks such
  // as Scroll Content render the same markup twice, once per breakpoint, so the
  // ids inside the duplicated column are not unique on the page. Looking a
  // player up by id would always resolve to whichever copy comes first in the
  // DOM, which on desktop is the hidden mobile copy.
  function getVimeoElements(videoElement) {
    const $video = $(videoElement);
    const $wrap = $video.closest(".video-wrap");
    const $button = $wrap.find(".vimeo-button").first();
    const $error = $wrap.find(".video-wrap__error").first();

    return { $button, $error, $video, $wrap };
  }

  function getVimeoIframe(videoElement) {
    const { $video } = getVimeoElements(videoElement);
    const element = $video.get(0);

    return (
      element?.tagName === "IFRAME" ? element : $video.find("iframe").get(0)
    );
  }

  function getVimeoDebugInfo(videoElement) {
    const { $video } = getVimeoElements(videoElement);
    const iframe = getVimeoIframe(videoElement);
    const iframeUrl = iframe?.src || null;

    return {
      playerId: $video.attr("id") || null,
      configuredUrl: $video.attr("data-vimeo-url") || iframeUrl,
      iframeUrl,
      pageUrl: window.location.href,
    };
  }

  function logVimeoError(context, videoElement, error) {
    const message = error?.message || String(error || "Unknown Vimeo error");

    console.error(`${LOG_PREFIX} ${context}: ${message}`, {
      ...getVimeoDebugInfo(videoElement),
      errorName: error?.name || null,
      rawError: error,
    });
  }

  function clearVimeoError(videoElement) {
    const { $error, $wrap } = getVimeoElements(videoElement);

    $wrap.removeClass("has-error");
    $error.attr("hidden", true).text("");
  }

  function showVimeoError(videoElement) {
    const { $button, $error, $wrap } = getVimeoElements(videoElement);
    const errorMessage =
      $wrap.attr("data-vimeo-error-message") ||
      "Sorry, this video can’t be played right now. Please try again later.";

    $wrap.removeClass("loading interacted").addClass("has-error");
    $button.show();
    $error.removeAttr("hidden").text(errorMessage);
  }

  function revealVimeoPlayer(videoElement) {
    const { $button, $wrap } = getVimeoElements(videoElement);

    clearVimeoError(videoElement);
    $button.hide();
    $wrap.addClass("interacted").removeClass("loading");
  }

  function initializeVimeoPlayers(isCookieConsentGiven) {
    console.debug(`${LOG_PREFIX} initializeVimeoPlayers called`, {
      isCookieConsentGiven,
    });

    // Check if the user has opted-in to cookies
    if (isCookieConsentGiven) {
      console.info(
        `${LOG_PREFIX} Required cookie group is active, initializing Vimeo players`,
      );

      // Loop through each .vimeo-video element and load the Vimeo player if the user has opted-in to cookies
      $(".vimeo-video").each(function () {
        // Each .vimeo-video element gets its own player, so duplicated markup
        // stays independent instead of sharing the first copy's player.
        var videoElement = this;

        if (vimeoPlayers.has(videoElement)) {
          console.debug(
            `${LOG_PREFIX} Vimeo player is already initialized`,
            getVimeoDebugInfo(videoElement),
          );
          return;
        }

        console.debug(
          `${LOG_PREFIX} Initializing Vimeo player`,
          getVimeoDebugInfo(videoElement),
        );

        let player;

        try {
          player = new Player(videoElement);
        } catch (error) {
          vimeoInitializationFailures.add(videoElement);
          logVimeoError("Error creating Vimeo player", videoElement, error);
          return;
        }

        vimeoPlayers.set(videoElement, player);

        player.on("error", function (error) {
          logVimeoError("Vimeo player error", videoElement, error);
        });

        player
          .ready()
          .then(function () {
            // Player is ready, log information about the player
            vimeoInitializationFailures.delete(videoElement);
            console.info(
              `${LOG_PREFIX} Vimeo player is ready`,
              getVimeoDebugInfo(videoElement),
            );
            clearVimeoError(videoElement);
          })
          .catch(function (error) {
            if (vimeoPlayers.get(videoElement) === player) {
              vimeoPlayers.delete(videoElement);
            }
            vimeoInitializationFailures.add(videoElement);
            logVimeoError("Error loading Vimeo player", videoElement, error);
          });
      });

      // Add click event listener to the .vimeo-button elements
      $(".vimeo-button")
        .off("click.videoWithOverlay")
        .on("click.videoWithOverlay", function (event) {
          // Resolve the player from this button's own sibling element so the
          // click always drives the copy the visitor can actually see.
          var videoElement = $(this).siblings(".vimeo-video").get(0);

          if (!videoElement) {
            console.error(
              `${LOG_PREFIX} Vimeo player not found: button has no video element`,
              this,
            );
            return;
          }

          console.debug(
            `${LOG_PREFIX} Vimeo button clicked`,
            getVimeoDebugInfo(videoElement),
          );

          const player = vimeoPlayers.get(videoElement);
          if (player) {
            event.preventDefault();
            clearVimeoError(videoElement);

            player
              .play()
              .then(() => {
                revealVimeoPlayer(videoElement);
              })
              .catch(function (error) {
                logVimeoError("Error playing Vimeo video", videoElement, error);
                showVimeoError(videoElement);
              });
          } else if (
            vimeoInitializationFailures.has(videoElement) &&
            getVimeoIframe(videoElement)
          ) {
            event.preventDefault();
            console.warn(
              `${LOG_PREFIX} Vimeo SDK initialization failed; revealing the native player`,
              getVimeoDebugInfo(videoElement),
            );
            revealVimeoPlayer(videoElement);
          } else {
            console.error(
              `${LOG_PREFIX} Vimeo player not found`,
              getVimeoDebugInfo(videoElement),
            );
            showVimeoError(videoElement);
          }
        });
    } else {
      console.warn(
        `${LOG_PREFIX} Required cookie group is not active; using external Vimeo links`,
        { group: VIDEO_COOKIE_CONSENT_GROUP },
      );

      // Without consent we keep playback available by turning the overlay button
      // into a normal outbound Vimeo link instead of embedding the player.
      // The vimeo button should open a new tab with the video URL
      $(".vimeo-button")
        .not("a")
        .each(function () {
          // Get the data-vimeo-url attribute of the current .vimeo-button element
          var videoUrl = $(this).siblings(".vimeo-video").data("vimeo-url");
          // Convert current button tag into 'a' tag
          $(this).replaceWith(function () {
            return $("<a>", {
              id: $(this).attr("id"),
              html: $(this).html(),
              class: $(this).attr("class"),
              href: videoUrl,
              target: "_blank",
              rel: "noopener noreferrer",
            });
          });
        });
    }
  }

  $(document).ready(function () {
    console.debug(`${LOG_PREFIX} Document ready`, { Player });

    let isInitialized = false;
    let boardflowReadyTimeoutId = null;

    const handleConsentApplied = (event) => {
      // OneTrust may dispatch multiple updates, so keep this handler idempotent.
      console.debug(`${LOG_PREFIX} handleConsentApplied called`, {
        data: event?.data,
      });

      if (isInitialized) {
        console.debug(
          `${LOG_PREFIX} Vimeo players are already initialized; returning early`,
        );
        return;
      }

      if (!window.OneTrust) {
        console.error(
          `${LOG_PREFIX} OneTrust is not available; Vimeo players cannot be initialized`,
        );
        initializeVimeoPlayers(false);
        return;
      }

      console.debug(`${LOG_PREFIX} OneTrust is available`);

      if (
        !window.boardflow.cookies ||
        typeof window.boardflow.cookies !== "object"
      ) {
        console.debug(
          `${LOG_PREFIX} Boardflow cookies helper is not ready yet; waiting for the next consent update`,
        );
        return;
      }

      if (
        typeof window.boardflow.cookies.shouldBeCCPACompliant !== "function"
      ) {
        console.warn(
          `${LOG_PREFIX} Boardflow cookie helper shouldBeCCPACompliant is unavailable; defaulting to standard Vimeo initialization`,
        );
        isInitialized = true;
        window.boardflow.removeEventListener(
          COOKIE_GROUPS_UPDATED_EVENT,
          handleConsentApplied,
        );
        initializeVimeoPlayers(true);
        return;
      }

      const shouldBeCCPACompliant =
        window.boardflow.cookies.shouldBeCCPACompliant();
      console.debug(`${LOG_PREFIX} CCPA compliance check`, {
        shouldBeCCPACompliant,
      });

      if (!shouldBeCCPACompliant) {
        console.info(
          `${LOG_PREFIX} CCPA is not required; initializing Vimeo players`,
        );
        isInitialized = true;
        window.boardflow.removeEventListener(
          COOKIE_GROUPS_UPDATED_EVENT,
          handleConsentApplied,
        );
        initializeVimeoPlayers(true);
        return;
      }

      if (typeof window.boardflow.cookies.isCookieConsentSet !== "function") {
        console.debug(
          `${LOG_PREFIX} Boardflow cookie helper isCookieConsentSet is not ready yet; waiting for the next consent update`,
        );
        return;
      }

      const isCookieConsentSet = window.boardflow.cookies.isCookieConsentSet();
      console.debug(`${LOG_PREFIX} Cookie consent set check`, {
        isCookieConsentSet,
      });

      if (isCookieConsentSet === false) {
        console.debug(
          `${LOG_PREFIX} Cookie consent is not set; waiting for an update`,
        );
        return;
      }

      if (typeof window.boardflow.cookies.isGroupActive !== "function") {
        console.debug(
          `${LOG_PREFIX} Boardflow cookie helper isGroupActive is not ready yet; waiting for the next consent update`,
        );
        return;
      }

      const isVideoCookieGroupActive = window.boardflow.cookies.isGroupActive(
        VIDEO_COOKIE_CONSENT_GROUP,
      );
      console.debug(`${LOG_PREFIX} Vimeo cookie group active check`, {
        group: VIDEO_COOKIE_CONSENT_GROUP,
        isVideoCookieGroupActive,
      });

      if (isVideoCookieGroupActive) {
        console.info(
          `${LOG_PREFIX} Vimeo cookie group is active; initializing`,
        );
        isInitialized = true;
        window.boardflow.removeEventListener(
          COOKIE_GROUPS_UPDATED_EVENT,
          handleConsentApplied,
        );
      } else {
        console.warn(
          `${LOG_PREFIX} Vimeo cookie group is not active; continuing to listen for updates`,
          { group: VIDEO_COOKIE_CONSENT_GROUP },
        );
      }

      initializeVimeoPlayers(isVideoCookieGroupActive);
    };

    const initializeBoardflowConsent = () => {
      if (!window.boardflow || typeof window.boardflow !== "object") {
        console.debug(
          `${LOG_PREFIX} Boardflow is not ready yet; waiting for the ready event`,
          { event: BOARDFLOW_READY_EVENT },
        );
        return false;
      }

      if (
        !window.boardflow.cookies ||
        typeof window.boardflow.cookies !== "object"
      ) {
        console.debug(
          `${LOG_PREFIX} Boardflow cookies helper is not ready yet; waiting for the ready event`,
          { event: BOARDFLOW_READY_EVENT },
        );
        return false;
      }

      console.debug(`${LOG_PREFIX} Boardflow is ready`);

      if (boardflowReadyTimeoutId) {
        window.clearTimeout(boardflowReadyTimeoutId);
        boardflowReadyTimeoutId = null;
      }

      console.debug(`${LOG_PREFIX} Registering cookie-group update listener`, {
        event: COOKIE_GROUPS_UPDATED_EVENT,
      });
      window.boardflow.addEventListener(
        COOKIE_GROUPS_UPDATED_EVENT,
        handleConsentApplied,
      );

      if (window.OneTrust) {
        console.debug(
          `${LOG_PREFIX} Consent state is already available; checking immediately`,
        );
        handleConsentApplied();
      } else {
        console.debug(
          `${LOG_PREFIX} Waiting for a cookie-group update before initialization`,
        );
      }

      return true;
    };

    if (!initializeBoardflowConsent()) {
      if (typeof window.boardflow?.addEventListener === "function") {
        window.boardflow.addEventListener(
          BOARDFLOW_READY_EVENT,
          initializeBoardflowConsent,
          {
            once: true,
          },
        );
      }

      boardflowReadyTimeoutId = window.setTimeout(() => {
        window.boardflow?.removeEventListener(
          BOARDFLOW_READY_EVENT,
          initializeBoardflowConsent,
        );

        if (initializeBoardflowConsent()) {
          return;
        }

        if (isInitialized) {
          return;
        }

        console.warn(
          `${LOG_PREFIX} Boardflow did not become ready within ${BOARDFLOW_READY_TIMEOUT_MS}ms; falling back to external Vimeo links`,
        );
        isInitialized = true;
        initializeVimeoPlayers(false);
      }, BOARDFLOW_READY_TIMEOUT_MS);
    }
  });
});
